import { Timestamp, GeoPoint, DocumentReference, doc } from "firebase/firestore";
import { db } from "@/lib/firebase";

export const TYPES = ["string", "number", "boolean", "timestamp", "null", "map", "array"];

export const typeOf = (v) => {
  if (v === null || v === undefined) return "null";
  if (v instanceof Timestamp) return "timestamp";
  if (v instanceof GeoPoint) return "geopoint";
  if (v instanceof DocumentReference) return "reference";
  if (Array.isArray(v)) return "array";
  if (typeof v === "object") return "map";
  return typeof v;
};

// Firestore value -> JSON-safe value (special types tagged so they round-trip).
export const toPlain = (v) => {
  const t = typeOf(v);
  if (t === "timestamp") return { __timestamp: v.toDate().toISOString() };
  if (t === "geopoint") return { __geopoint: [v.latitude, v.longitude] };
  if (t === "reference") return { __ref: v.path };
  if (t === "array") return v.map(toPlain);
  if (t === "map") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, toPlain(x)]));
  return v ?? null;
};

export const fromPlain = (v) => {
  if (Array.isArray(v)) return v.map(fromPlain);
  if (v && typeof v === "object") {
    if (typeof v.__timestamp === "string") return Timestamp.fromDate(new Date(v.__timestamp));
    if (Array.isArray(v.__geopoint)) return new GeoPoint(v.__geopoint[0], v.__geopoint[1]);
    if (typeof v.__ref === "string") return doc(db, v.__ref);
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, fromPlain(x)]));
  }
  return v;
};

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

// Firestore value -> editor draft string/bool for a given type.
export const toDraft = (v, type) => {
  if (type === "boolean") return Boolean(v);
  if (type === "timestamp") return v instanceof Timestamp ? toLocalInput(v.toDate()) : toLocalInput(new Date());
  if (type === "null") return "";
  if (["map", "array", "geopoint", "reference"].includes(type)) return JSON.stringify(toPlain(v ?? (type === "array" ? [] : {})), null, 2);
  return v == null ? "" : String(v);
};

// Editor draft -> Firestore value. Throws on invalid input.
export const fromDraft = (draft, type) => {
  if (type === "string") return String(draft);
  if (type === "number") { const n = Number(draft); if (draft === "" || Number.isNaN(n)) throw new Error("Not a valid number"); return n; }
  if (type === "boolean") return Boolean(draft);
  if (type === "null") return null;
  if (type === "timestamp") { const d = new Date(draft); if (isNaN(d)) throw new Error("Invalid date"); return Timestamp.fromDate(d); }
  let parsed;
  try { parsed = JSON.parse(draft); } catch { throw new Error("Invalid JSON"); }
  if (type === "array" && !Array.isArray(parsed)) throw new Error("Value must be a JSON array");
  if (type === "map" && (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))) throw new Error("Value must be a JSON object");
  return fromPlain(parsed);
};

export const preview = (v, max = 80) => {
  const t = typeOf(v);
  if (t === "null") return "null";
  if (t === "timestamp") return v.toDate().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "medium" });
  if (t === "geopoint") return `${v.latitude}, ${v.longitude}`;
  if (t === "reference") return v.path;
  if (t === "array") return `[${v.length}] ${JSON.stringify(toPlain(v)).slice(0, max)}`;
  if (t === "map") return `{${Object.keys(v).length}} ${JSON.stringify(toPlain(v)).slice(0, max)}`;
  const s = String(v);
  return s.length > max ? s.slice(0, max) + "…" : s;
};

export const forLog = (v) => (v === undefined ? null : JSON.stringify(toPlain(v)).slice(0, 2000));
